import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, ChevronRight, Copy, Pencil, Eye, Slack, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { Progress } from "@/components/ui/progress";
import { format } from "@/lib/brand-tokens";
import { deals, brands, getBrandById, getAdminById, getReservationsByDeal } from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { publishDealToSlack } from "@/lib/slackClient";
import { toast } from "sonner";

// ADM-02 — Deals list
const STATUS_TABS = ["All", "Draft", "Review", "Published", "Closed"];

export default function AdminDealsListPage() {
  const [tab, setTab] = useState("All");
  const [brandFilter, setBrandFilter] = useState("all");
  const [q, setQ] = useState("");
  const [slackPostingId, setSlackPostingId] = useState(null);
  const brandsById = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), []);

  const sendToSlack = async (deal) => {
    setSlackPostingId(deal.id);
    try {
      const r = await publishDealToSlack(deal, brandsById);
      toast.success(`Posted to Slack`, { description: `${deal.name} → #${r.channel}` });
    } catch (e) {
      const msg = String(e?.message || e);
      if (msg.includes("not configured")) {
        toast.error("Slack not configured", { description: "Configure a default channel in Settings → Slack first." });
      } else {
        toast.error("Slack post failed", { description: msg });
      }
    } finally {
      setSlackPostingId(null);
    }
  };

  const filtered = useMemo(() => {
    return deals.filter((d) => {
      if (tab !== "All" && d.status !== tab) return false;
      if (brandFilter !== "all" && d.brandId !== brandFilter) return false;
      if (q && !d.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [tab, brandFilter, q]);

  return (
    <div data-testid="admin-deals-page" className="space-y-5 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Inventory</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Deals</h1>
          <p className="text-body-sm text-ink-muted mt-1">{filtered.length} of {deals.length} deals · all states</p>
        </div>
        <Link to="/admin/deals/new">
          <Gated action="deals.create">
            <Button className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="new-deal-btn">
              <Plus className="size-4 mr-1.5" /> New deal
            </Button>
          </Gated>
        </Link>
      </div>

      <div className="-mx-8 bg-surface border-y border-line">
        <div className="px-8 py-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="flex bg-surface-alt rounded-md p-0.5">
            {STATUS_TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`deal-tab-${t}`}
                      className={`px-3 py-1.5 rounded-md text-caption font-semibold transition-all ${
                        tab === t ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>
                {t}{t !== "All" && <span className="ml-1.5 text-ink-hint tabular-nums">({deals.filter((d) => d.status === t).length})</span>}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by deal name…" className="pl-8 h-9 bg-surface-alt border-transparent focus-visible:bg-surface focus-visible:border-line" />
          </div>
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-line bg-surface text-body-sm">
            <option value="all">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Deal</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Rate</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Cap usage</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Reservations</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Deadline</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Owner</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((d) => {
                const brand = getBrandById(d.brandId);
                const owner = getAdminById(d.createdBy);
                const fillPct = Math.round((d.capUsed / d.capTotal) * 100);
                const resCount = getReservationsByDeal(d.id).length;
                return (
                  <tr key={d.id} className="hover:bg-surface-alt/40">
                    <td className="px-8 py-3.5">
                      <Link to={`/admin/deals/${d.id}`} className="flex items-center gap-3 group">
                        <BrandStack brandIds={d.brandIds} size="sm" max={3} />
                        <div className="min-w-0">
                          <div className="text-ink font-semibold group-hover:text-brand-700 transition-colors flex items-center gap-2">
                            <span className="truncate max-w-[300px]">{d.name}</span>
                            {d.isMultiBrand && <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-50 border border-brand-100 px-1 py-px rounded shrink-0">Multi</span>}
                          </div>
                          <div className="text-caption text-ink-subtle truncate max-w-[260px]">
                            {d.isMultiBrand
                              ? d.brandIds.map(id => getBrandById(id)?.name).filter(Boolean).join(" + ")
                              : brand?.name}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3.5"><StatusBadge entity="deal" status={d.status} dot /></td>
                    <td className="px-3 py-3.5 text-ink font-semibold tabular-nums text-right">{d.rate}%</td>
                    <td className="px-3 py-3.5 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <Progress value={fillPct} className="h-1 flex-1" />
                        <span className="text-caption text-ink-muted tabular-nums whitespace-nowrap">{fillPct}%</span>
                      </div>
                      <div className="text-caption text-ink-subtle tabular-nums mt-1">{format.currencyCompact(d.capUsed)} / {format.currencyCompact(d.capTotal)}</div>
                    </td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{resCount}</td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">{d.deadline ? format.date(d.deadline) : "—"}</td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption">{owner ? `${owner.firstName} ${owner.lastName[0]}.` : "—"}</td>
                    <td className="px-8 py-3.5 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <IconBtn to={`/admin/deals/${d.id}`} icon={<Eye className="size-3.5" />} label="View" />
                        <IconBtn to={`/admin/deals/${d.id}/edit`} icon={<Pencil className="size-3.5" />} label="Edit" />
                        <IconBtn icon={<Copy className="size-3.5" />} label="Clone" />
                        {d.status === "Published" && (
                          <Gated action="deals.publish">
                            <button
                              onClick={() => sendToSlack(d)}
                              disabled={slackPostingId === d.id}
                              data-testid={`send-to-slack-row-${d.id}`}
                              className="size-7 grid place-items-center rounded text-ink-muted hover:bg-[#4A154B]/10 hover:text-[#4A154B] transition-colors disabled:opacity-50"
                              title="Send to Slack"
                            >
                              {slackPostingId === d.id ? <Loader2 className="size-3.5 animate-spin" /> : <Slack className="size-3.5" />}
                            </button>
                          </Gated>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ to, icon, label }) {
  const cls = "size-7 grid place-items-center rounded text-ink-muted hover:bg-surface-alt hover:text-ink transition-colors";
  if (to) return <Link to={to} className={cls} title={label}>{icon}</Link>;
  return <button className={cls} title={label}>{icon}</button>;
}
